import {
  Badge,
  Box,
  HStack,
  Heading,
  Skeleton,
  Stack,
  Text,
} from "@chakra-ui/react";
import * as React from "react";
import { useParams } from "react-router-dom";

import { getLinkedinCampaign } from "../api/integration";
import { getLinkedinAgent } from "../api/linkedin-agent";
import { notFoundToNull } from "../api/not-found";
import { useBreadcrumbs } from "../components/breadcrumbs";
import { ChangeCell } from "../components/change-cell";
import DataTable from "../components/data-table";
import { LinkedinAgentSwitch } from "../components/linkedin-agent-switch";
import { LinkedinCampaignStatusPill } from "../components/linkedin-campaign-status-pill";
import Page from "../components/page";
import PageBody from "../components/page-body";
import { UtcTimestamp } from "../components/utc-timestamp";
import {
  useAppDispatch,
  useAppSelector,
  useLoading,
  useWorkspaceURL,
} from "../hooks";
import { useLinkedinChanges } from "../hooks/linkedin-changes";
import { brand } from "../lib/brand";
import { formatStatus } from "../lib/format";
import { isSupportedLinkedinCampaign } from "../lib/linkedin-metrics";
import {
  loadLinkedinAdAccounts,
  selectLinkedinAdAccountById,
} from "../store/linkedinSlice";
import {
  LinkedinAgent,
  LinkedinAgentPauseReason,
  LinkedinAgentStatus,
  LinkedinCampaign,
} from "../types/linkedin";

// Only the reasons the agent stopped on its own are worth a word: the
// switch already says it's off when the customer turned it off.
const formatPauseReason = (
  reason: LinkedinAgentPauseReason | null,
): string | null => {
  switch (reason) {
    case LinkedinAgentPauseReason.CAMPAIGN_INACTIVE:
      return "Campaign is inactive";
    case LinkedinAgentPauseReason.CAMPAIGN_WITHOUT_BUDGET:
      return "Campaign has no daily budget";
    default:
      return null;
  }
};

export default function LinkedInAgentPage() {
  const dispatch = useAppDispatch();
  const { workspace: workspaceSlug, adAccountId, campaignId } = useParams();
  const workspaceURL = useWorkspaceURL();

  const [agent, setAgent] = React.useState<LinkedinAgent | null>(null);
  const [campaign, setCampaign] = React.useState<LinkedinCampaign | null>(null);
  const [isLoading, withLoading] = useLoading();
  const [hasLoadedOnce, setHasLoadedOnce] = React.useState(false);
  const [changesVersion, setChangesVersion] = React.useState(0);

  const adAccount = useAppSelector((state) =>
    selectLinkedinAdAccountById(parseInt(adAccountId || "0"))(state),
  );
  const adAccountsLoaded = useAppSelector(
    (state) => state.linkedin.adAccounts.length > 0,
  );

  useBreadcrumbs([
    { label: "Dashboard", to: workspaceURL("") },
    { label: "LinkedIn", to: workspaceURL("linkedin-ad-accounts") },
    {
      label: adAccount?.name ?? "…",
      to: workspaceURL(`linkedin-ad-accounts/${adAccountId}/campaigns`),
    },
    { label: campaign?.name ?? "…" },
  ]);

  const load = React.useCallback(async () => {
    await withLoading(async () => {
      const [campaignData, agentData] = await Promise.all([
        getLinkedinCampaign(
          workspaceSlug,
          parseInt(adAccountId),
          parseInt(campaignId),
        ),
        notFoundToNull(() =>
          getLinkedinAgent(
            workspaceSlug,
            parseInt(adAccountId),
            parseInt(campaignId),
          ),
        ),
      ]);
      setCampaign(campaignData);
      setAgent(agentData);
      setHasLoadedOnce(true);
    });
  }, [adAccountId, campaignId, withLoading, workspaceSlug]);

  React.useEffect(() => {
    load();
  }, [load]);

  React.useEffect(() => {
    if (!adAccountsLoaded) {
      dispatch(loadLinkedinAdAccounts());
    }
  }, [adAccountsLoaded, dispatch]);

  const handleAgentChange = React.useCallback(async () => {
    await load();
    setChangesVersion((version) => version + 1);
  }, [load]);

  const pauseReason =
    agent?.status === LinkedinAgentStatus.PAUSED
      ? formatPauseReason(agent["pause-reason"])
      : null;

  return (
    <Page loading={isLoading && !hasLoadedOnce}>
      <PageBody>
        <Stack flex="1" gap="6">
          <HStack align="flex-start" gap="4" wrap="wrap">
            <Stack flex="1" gap="2" minWidth="0">
              <Text
                color="fg.ink"
                fontSize={"22px" as any}
                fontWeight="medium"
                lineHeight={"1.35" as any}
                wordBreak="break-word"
              >
                {campaign?.name ?? <Skeleton w="2xs" h="6" />}
              </Text>
              {campaign && (
                <HStack color="fg.muted" fontSize="sm" gap="2.5" wrap="wrap">
                  <LinkedinCampaignStatusPill status={campaign.status} />
                  <Text>{formatStatus(campaign.objective)}</Text>
                  {!isSupportedLinkedinCampaign(campaign) && (
                    <Badge
                      colorPalette="gray"
                      size="xs"
                      textTransform="uppercase"
                      variant="subtle"
                    >
                      No daily budget
                    </Badge>
                  )}
                  {pauseReason && (
                    <>
                      <Text aria-hidden>·</Text>
                      <Text>{pauseReason}</Text>
                    </>
                  )}
                </HStack>
              )}
            </Stack>
            {campaign && (
              <HStack
                background="white"
                borderRadius="l2"
                boxShadow="card"
                flexShrink="0"
                gap="3"
                height="10"
                paddingLeft="3.5"
                paddingRight="3"
              >
                <Text fontSize="sm" fontWeight="medium">
                  {brand.productName}
                </Text>
                <LinkedinAgentSwitch
                  adAccountId={parseInt(adAccountId!)}
                  campaignId={campaign.id}
                  campaignName={campaign.name}
                  disabled={!isSupportedLinkedinCampaign(campaign)}
                  on={agent?.status === LinkedinAgentStatus.ACTIVE}
                  onToggled={handleAgentChange}
                  workspace={workspaceSlug!}
                />
              </HStack>
            )}
          </HStack>

          {adAccountId && campaignId && (
            <CampaignChanges
              adAccountId={parseInt(adAccountId)}
              campaignId={parseInt(campaignId)}
              version={changesVersion}
              workspace={workspaceSlug!}
            />
          )}
        </Stack>
      </PageBody>
    </Page>
  );
}

function CampaignChanges({
  adAccountId,
  campaignId,
  version,
  workspace,
}: {
  adAccountId: number;
  campaignId: number;
  version: number;
  workspace: string;
}) {
  const feed = useLinkedinChanges(workspace, adAccountId, {
    campaignId,
    version,
  });

  return (
    <Stack gap="4">
      <HStack justify="space-between">
        <Heading textStyle="h2">Changes</Heading>
        <Text color="fg.muted" fontSize="sm">
          {feed.total} changes in the last 30 days
        </Text>
      </HStack>
      {feed.total === 0 && !feed.isLoading ? (
        <Box background="white" borderRadius="l3" boxShadow="card" padding="6">
          <Text color="fg.muted" fontSize="sm">
            No changes yet.
          </Text>
        </Box>
      ) : (
        <DataTable
          data={feed.rows}
          skeleton={feed.isLoading && feed.rows.length === 0 ? 3 : undefined}
          template="170px minmax(0,1fr)"
          minTableWidth="560px"
          pagination={feed.pagination}
          columns={[
            {
              name: "Date (UTC)",
              cell: ({ value }) => (
                <UtcTimestamp color="fg.muted" date={value["created-at"]} />
              ),
            },
            {
              name: "Change",
              cell: ({ value }) => <ChangeCell change={value} />,
            },
          ]}
        />
      )}
    </Stack>
  );
}
